/**
 * OaSIS Explorer Frontend - Version-Aware Field Mapping
 * Task 33.5: New Abilities Integration
 * 
 * Handles field name changes between dataset versions.
 * Maps old field names to new ones when switching versions.
 */

import { getFieldFullName, getFieldShorthand } from './fieldShorthandMapping';
import { isAllowedInterestFilterField } from './interestFilterFields';

/**
 * Field renames between versions
 * Key: old field name, Value: { [version]: new field name }
 */
const FIELD_RENAMES: Record<string, Record<string, string>> = {
  // Motor Coordination was renamed to Hand-Eye Coordination in 2025
  motorCoordination: {
    '2025': 'handEyeCoordination',
  },
  // Reverse mapping for 2023
  handEyeCoordination: {
    '2023': 'motorCoordination',
  },
};

function normalizeVersion(version: string): string {
  return version.endsWith('_adv') ? version.replace(/_adv$/, '') : version;
}

/**
 * Map a field name to its version-specific equivalent
 * 
 * @param fieldName - Original field name
 * @param version - Target dataset version ('2023', '2025', '2025_adv', etc.)
 * @returns Mapped field name for the target version
 * 
 * @example
 * ```typescript
 * mapFieldToVersion('motorCoordination', '2025'); // 'handEyeCoordination'
 * mapFieldToVersion('motorCoordination', '2025_adv'); // 'handEyeCoordination'
 * mapFieldToVersion('handEyeCoordination', '2023'); // 'motorCoordination'
 * mapFieldToVersion('leadership', '2025'); // 'leadership' (unchanged)
 * ```
 */
export function mapFieldToVersion(fieldName: string, version: string): string {
  const normalizedVersion = normalizeVersion(version);
  const renames = FIELD_RENAMES[fieldName];
  if (renames && renames[normalizedVersion]) {
    return renames[normalizedVersion];
  }
  return fieldName;
}

/**
 * Map all measurement filters to their version-specific equivalents
 * 
 * @param measurements - Record of field names to filter values
 * @param version - Target dataset version
 * @returns New measurements object with mapped field names
 */
export function mapMeasurementsToVersion(
  measurements: Record<string, { min?: number; max?: number; exact?: number | string }>,
  version: string
): Record<string, { min?: number; max?: number; exact?: number | string }> {
  const mapped: Record<string, { min?: number; max?: number; exact?: number | string }> = {};
  
  for (const [fieldName, value] of Object.entries(measurements)) {
    const mappedFieldName = mapFieldToVersion(fieldName, version);
    if (!isAllowedInterestFilterField(mappedFieldName)) {
      continue;
    }
    mapped[mappedFieldName] = value;
  }
  
  return mapped;
}

/**
 * Check if a field exists in a specific version
 * 
 * @param fieldName - Field name to check
 * @param version - Dataset version
 * @returns true if the field exists in the version
 */
export function fieldExistsInVersion(fieldName: string, version: string): boolean {
  const normalizedVersion = normalizeVersion(version);
  const excludedFieldsByVersion: Record<string, string[]> = {
    '2023': ['generalLearningAbility', 'formPerception', 'handEyeCoordination'],
    '2025': ['motorCoordination'],
  };

  const excludedFields = excludedFieldsByVersion[normalizedVersion];
  if (!excludedFields) {
    return true;
  }

  return !excludedFields.includes(fieldName);
}

/**
 * Filter out measurements that don't exist in the target version
 * 
 * @param measurements - Record of field names to filter values
 * @param version - Target dataset version
 * @returns Filtered measurements with only valid fields for the version
 */
export function filterMeasurementsForVersion(
  measurements: Record<string, { min?: number; max?: number; exact?: number | string }>,
  version: string
): Record<string, { min?: number; max?: number; exact?: number | string }> {
  const filtered: Record<string, { min?: number; max?: number; exact?: number | string }> = {};
  
  for (const [fieldName, value] of Object.entries(measurements)) {
    // First map to version-specific name
    const mappedFieldName = mapFieldToVersion(fieldName, version);
    if (!isAllowedInterestFilterField(mappedFieldName)) {
      continue;
    }
    
    // Then check if it exists in the version
    if (fieldExistsInVersion(mappedFieldName, version)) {
      filtered[mappedFieldName] = value;
    }
  }
  
  return filtered;
}

/**
 * Normalize measurement query params for a target version.
 *
 * - Drops measurement filters that do not exist in the target version
 * - Rewrites renamed fields to their version-specific equivalents
 * - Rewrites full keys to shorthand when available
 */
export function sanitizeMeasurementParamsForVersion(
  params: URLSearchParams,
  version: string
): { params: URLSearchParams; changed: boolean } {
  const nextParams = new URLSearchParams(params);
  const reservedParams = new Set(['q', 'page', 'limit', 'pref', 'sort', 'view']);
  let changed = false;

  const isLikelyMeasurementValue = (rawValue: string): boolean => {
    const value = rawValue.trim();
    return (
      /^>=-?\d+(?:\.\d+)?$/.test(value) ||
      /^<=-?\d+(?:\.\d+)?$/.test(value) ||
      /^-?\d+(?:\.\d+)?$/.test(value) ||
      /^-?\d+(?:\.\d+)?--?\d+(?:\.\d+)?$/.test(value) ||
      /^[A-Za-z]$/.test(value) ||
      /^[A-Za-z](?:,[A-Za-z])+$/i.test(value)
    );
  };

  const entries = Array.from(params.entries());
  for (const [key, value] of entries) {
    if (reservedParams.has(key)) continue;
    if ((key.startsWith('h') && key.length === 2 && /^h\d$/.test(key)) ||
        (key.startsWith('t') && key.length === 2 && /^t\d$/.test(key))) {
      continue;
    }

    const fieldName = getFieldFullName(key) || key;
    const isShorthand = fieldName !== key;
    const hasShorthand = getFieldShorthand(fieldName) !== null;
    const looksLikeMeasurement = isLikelyMeasurementValue(value);
    if (!isShorthand && !hasShorthand && !looksLikeMeasurement) {
      continue;
    }

    const mappedFieldName = mapFieldToVersion(fieldName, version);
    if (!isAllowedInterestFilterField(mappedFieldName)) {
      nextParams.delete(key);
      changed = true;
      continue;
    }
    if (!fieldExistsInVersion(mappedFieldName, version)) {
      nextParams.delete(key);
      changed = true;
      continue;
    }

    const targetKey = getFieldShorthand(mappedFieldName) ?? mappedFieldName;
    if (targetKey !== key) {
      nextParams.delete(key);
      nextParams.set(targetKey, value);
      changed = true;
    }
  }

  return { params: nextParams, changed };
}
